import React from 'react';
import { Card, Table, Typography, Button, Space, Tag, App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import spotPurchaseService from '@/services/spotPurchase.service';

const { Title, Text } = Typography;

/** Eligible PRs for Spot / Market Purchase — purchasers can self-pick. */
const SpotPurchaseSupervisorPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['spot-purchase', 'eligible-prs'],
    queryFn: () => spotPurchaseService.listEligiblePrs(),
  });

  const claimMutation = useMutation({
    mutationFn: (prId: string) => spotPurchaseService.claim(prId),
    onSuccess: () => {
      message.success('Picked — you are self-assigned on this PR');
      queryClient.invalidateQueries({ queryKey: ['spot-purchase', 'eligible-prs'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not pick PR'),
  });

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Spot / Market Purchase
          </Title>
          <Text type="secondary">
            Approved PRs with non-critical lines. Purchasers pick a PR to self-assign, then reserve and buy
            (mobile or from the PR).
          </Text>
        </div>
        <Button onClick={() => refetch()}>Refresh</Button>
      </Space>
      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data as any[]}
          columns={[
            {
              title: 'PR',
              dataIndex: 'pr_number',
              render: (v: string, row: any) => (
                <Button type="link" onClick={() => navigate(`/scm/procurement/purchase-requests/${row.id}`)}>
                  {v}
                </Button>
              ),
            },
            { title: 'Status', dataIndex: 'pr_status' },
            { title: 'Priority', dataIndex: 'priority' },
            { title: 'Required', dataIndex: 'required_date' },
            { title: 'Eligible lines', dataIndex: 'eligible_line_count' },
            { title: 'Total lines', dataIndex: 'line_count' },
            {
              title: 'You',
              key: 'self',
              render: (_: unknown, row: any) =>
                Number(row.self_assigned) === 1 || row.self_assigned === true ? (
                  <Tag color="teal">Picked</Tag>
                ) : (
                  <Tag>Open</Tag>
                ),
            },
            {
              title: '',
              key: 'actions',
              render: (_: unknown, row: any) => {
                const picked = Number(row.self_assigned) === 1 || row.self_assigned === true;
                return (
                  <Space>
                    {!picked && (
                      <Button
                        type="primary"
                        size="small"
                        loading={claimMutation.isPending}
                        onClick={() => claimMutation.mutate(row.id)}
                      >
                        Pick / self-assign
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() => navigate(`/scm/procurement/purchase-requests/${row.id}`)}
                    >
                      Open PR
                    </Button>
                  </Space>
                );
              },
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default SpotPurchaseSupervisorPage;
